import React, { useEffect, useRef, useState } from 'react';
import { Box, List, ListItem, Typography, useMediaQuery } from '@mui/material';
import { LOANS_APPLICATION } from "../locales/namespaces";
import { useTranslation } from "react-i18next";
import useUserData from "../../../hooks/user/useUserData";
import CheckBox from "../../../assets/images/icons/checkBox";
import Dropdown from "../../../assets/images/icons/dropdown";
import { BorderColor } from '@mui/icons-material';
import { unset } from 'lodash';
import { motion, AnimatePresence } from 'framer-motion';

const styles = {
    '&.MuiList-root': {
        position: 'relative',
        paddingTop: { xs: '8px !important', sm: '8px !important', md: '0px !important' }
    },
    '.MuiListItem-root': {
        '.active .sub-item, &.active:not(.with-sub) .item, &.active .index-item': {
            backgroundColor: '#485476',
            color: '#fff',
        },
        '.item': {
            border: '0.6px solid #DAE0F0',
            cursor: 'pointer',
            color: '#485476',
            backgroundColor: '#ffff',
            fontSize: '15px',
            borderRadius: '10px',
            width: '100%',
            zIndex: 10,

        },
        '.item::before': {
            content: '""',
            position: 'absolute',
            left: '-4px',
            top: '0% ',
            width: '1px',
            height: '100%',
            backgroundImage: 'repeating-linear-gradient(to bottom,#CAD1E5,#CAD1E5 8px,transparent 8px,transparent 16px)'
        },
        '&.MuiListItem-root:first-of-type .item::before': {
            top: '20px',
        },
        '&.MuiListItem-root:last-of-type .item::before': {
            top: '-30% ',
            height: '50%',
        },
        '.index-item': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: '0.6px solid #DAE0F0',
            backgroundColor: 'white',
            position: 'absolute',
            left: '-28px',
            top: '0px',
            margin: '10px',
            boxShadow: '0px 0px 0px 4px #f5f7fd',
            fontSize: '13px',
            zIndex: '20'
        },
        '&.active .index-item': {
            backgroundColor: '#485476 !important',
            color: '#fff !important',
        },
        '.sub-item': {
            cursor: 'pointer',
            padding: '10px 10px',
            color: '#485476',
            backgroundColor: '#ffff',
            fontSize: '13px',
            borderRadius: '8px',
            width: '100%',
            zIndex: 10,
        },
        '&.with-sub .drop-down svg path': {
            fill: '#485476'
        },
        '&.active .drop-down svg': {
            transform: 'rotate(180deg)',
        },
        '&.active .drop-down svg path': {
            fill: '#ffff'
        },
        '&.active.with-sub .drop-down svg path': {
            fill: '#485476'
        },
        '&.active.with-sub .drop-down svg': {
            transform: 'rotate(180deg)',
        },
        '.all-filled p': {
            backgroundColor: 'white',
            color: '#0BC1B6',
            border: 'initial'
        },
        '.all-filled p svg path': {
            fill: '#0BC1B6'
        },
        '.all-filled': {
            border: 'unset'
        },
        '.check-box': {
            display: 'none'
        },
        '.check-box svg': {
            height: '18px',
            width: '18px'
        },
        '.check-box svg path': {
            fill: 'white',
        },
        '.all-filled .check-box': {
            display: 'inline',
            lineHeight: '0',
            paddingRight: '4px'
        },
        '&.with-sub': {
            marginBottom: '0px'
        },
        '&.subItem': {
            padding: '0 0 10px 0',
        },
        '&.subItem p': {
            fontSize: '13px'
        },
        '&:last-child': {
            marginBottom: '0'
        },
    },
};


export default function WizardSidebar({ formFields, activeIndex, setActiveIndex, activeIndexSubItem, setActiveIndexSubItem, formErrors, allQuestionsInOneArray, className }) {
    const matchDownMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const subItemRefs = useRef({});
    const { t } = useTranslation(LOANS_APPLICATION)
    const [groupColorsByIndex, setGroupColorsByIndex] = useState({});
    const itemRefs = useRef([]);
    const [animationDone, setAnimationDone] = useState(false);
    const updateIndex = (index, subIndex) => {
        setActiveIndex(index);
        setActiveIndexSubItem(subIndex);
    };
    const { language } = useUserData()

    const getGroupColor = (groupId, repeatNum) => {
        if (!formErrors) {
            return '#DAE0F0';
        }
        const requiredFields = allQuestionsInOneArray.filter(
            item =>
                item.GroupId === groupId &&
                item.GroupRepeatNum === repeatNum &&
                item.required
        );
        const totalRequired = requiredFields.length;
        let color = '#0BC1B6';
        let missingCount = 0;
        let errorCount = 0;
        requiredFields.forEach(field => {
            const key = `${field.QuestionId}_${field.GroupId}_${field.GroupRepeatNum}`;            
            const err = formErrors[key];

            if (err === t('Required')) {
                missingCount++;
            } else if (
                err === t('Invalid email') ||
                err === t('Must be a number') ||
                err === t('Invalid format') 
            ) {
                errorCount++;
            }
        });
        if (totalRequired === 0) color = '#0BC1B6';
        else if (missingCount === totalRequired) color = '#DAE0F0';
        else if (missingCount > 0) color = '#485476';
        else if (errorCount > 0) color = '#DBB2AF';
        return color;
    };


    useEffect(() => {
        if (!formFields || !Array.isArray(formFields)) return;

        const SUCCESS_COLOR = '#0BC1B6';
        const newColors = {};

        formFields.forEach((category, index) => {
            const subgroups = category.questions?.subgroups || [];

            if (subgroups.length > 0) {
                const subgroupColors = subgroups.map((sub) => getGroupColor(sub.GrouId, sub.GroupRepeatNum ));


                const allAreSuccess = subgroupColors.every(color => color === SUCCESS_COLOR);

                if (allAreSuccess) {
                    newColors[index] = SUCCESS_COLOR;
                } else {
                    const firstDifferent = subgroupColors.find(color => (color !== SUCCESS_COLOR && color !== '#DAE0F0'));
                    newColors[index] = firstDifferent || '#DAE0F0';
                }
            } else {
                const mainColor = getGroupColor(category.questions.GrouId, category.questions.GroupRepeatNum);
                newColors[index] = mainColor;
            }
        });

        setGroupColorsByIndex(newColors);
    }, [formErrors, formFields]);

    // Start ScrollTo on click on menu items
    useEffect(() => {
        if (!matchDownMD) return;
        

        const mainEl = itemRefs.current[activeIndex];
        const container = mainEl?.parentElement;

        if (mainEl && container) {
            const containerWidth = container.offsetWidth;
            const itemWidth = mainEl.offsetWidth;

            const offsetLeft = mainEl.offsetLeft - container.offsetLeft;
            const scrollLeft = offsetLeft - (containerWidth / 2) + (itemWidth / 2);

            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth',
            });
        }

    }, [activeIndex, matchDownMD]);


    useEffect(() => {
        if (!matchDownMD) return;

        const subKey = `${activeIndex}-${activeIndexSubItem}`;
        const subEl = subItemRefs.current[subKey];

        if (subEl && subEl.offsetParent) {
            const container = subEl.offsetParent;

            const containerWidth = container.offsetWidth;
            const itemWidth = subEl.offsetWidth;

            const offsetLeft = subEl.offsetLeft - container.offsetLeft;
            const scrollLeft = offsetLeft - (containerWidth / 2) + (itemWidth / 2);

            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
            });
        }
    }, [activeIndexSubItem, matchDownMD]);


    useEffect(() => {
        //hide hover menu items on Animation
        setAnimationDone(false)
        setTimeout(() => {
			setAnimationDone(true)
		}, 800);

        if (matchDownMD) return;
        const el = itemRefs.current[activeIndex];
        if (!el) return;

        setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    }, [activeIndex, matchDownMD]);
    // End ScrollTo on click on menu items





    if (!Array.isArray(formFields)) return null;

    return (
        <Box className={className} sx={{ paddingLeft: { xs: 0, md: '20px' } }}>
            <List sx={styles}>
                {formFields.map((category, index) => {
                    const isActive = index === activeIndex;
                    const subgroups = category.questions?.subgroups || [];

                    return (
                        <ListItem key={index}
                            sx={{ paddingRight: '0px', paddingTop: '0px' }}
                            ref={(el) => (itemRefs.current[index] = el)}
                            className={(isActive ? 'active ' : ' list-item ') + (subgroups.length > 0 ? 'with-sub' : '')}
                        >
                            <span className='index-item'
                                style={{
                                    backgroundColor: groupColorsByIndex[index] === '#0BC1B6' ? groupColorsByIndex[index] : '',
                                    color: groupColorsByIndex[index] === '#0BC1B6' ? '#fff' : undefined,
                                }}>
                                {index + 1}
                            </span>
                            <Box className='item'
                                sx={{
                                    border: `0.6px solid ${groupColorsByIndex[index]} !important`,
                                    borderRadius: '8px',
                                    backgroundColor: `${groupColorsByIndex[index] === '#0BC1B6'
                                            ? groupColorsByIndex[index]
                                            : ''
                                        } !important`,
                                    ...(groupColorsByIndex[index] === '#0BC1B6'
                                        ? { color: '#fff !important' }
                                        : {}),
                                }}
                            >
                                <Box role="button" tabIndex={0} className="cnt-title" style={{ display: 'block', padding: '10px' }} onClick={() => { subgroups.length === 0 ? updateIndex(index) : updateIndex(index, 0) }}>
                                    <Box component="span" className='flex-center-left'>
                                        {(language === 'en' ? category.questions?.DescriptionEN : category.questions?.DescriptionEL)}
                                        <Box component="span" className="drop-down"
                                            sx={{
                                                paddingLeft: '10px', marginLeft: 'auto', alignSelf: 'center',
                                                'svg': { height: '13px', width: '13px' },
                                                ...(groupColorsByIndex[index] === '#0BC1B6' && {
                                                    path: {
                                                        fill: '#fff !important',
                                                    }
                                                }),
                                            }}
                                        >
                                            <Dropdown />
                                        </Box>

                                    </Box>

                                    <AnimatePresence initial={false}>
                                        {(subgroups.length > 0 && index === activeIndex && !matchDownMD) &&
                                            <motion.div
                                                key="subgroups"
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: "auto" }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                style={{ overflow: "hidden" }}
                                                className={`${animationDone ? "animation-done" : ""}`}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'block',
                                                        border: {
                                                            xs: `0.6px solid ${groupColorsByIndex[index]} !important`,
                                                            sm: `0.6px solid ${groupColorsByIndex[index]} !important`,
                                                            md: 'none !important'
                                                        },
                                                        borderRadius: '8px'
                                                    }}
                                                    className="subgroups-cnt">
                                                    {subgroups.map((sub, subIndex) => {
                                                        const isSubActive = index === activeIndex && activeIndexSubItem === subIndex;
                                                        return (
                                                            <Box
                                                                role="button" tabIndex={0} 
                                                                key={`sub-${index}-${subIndex}`}
                                                                sx={{
                                                                    ...((getGroupColor(sub.GrouId, sub.GroupRepeatNum) && (getGroupColor(sub.GrouId, sub.GroupRepeatNum) !== '#DAE0F0') )&& { border: '0.6px solid ' + getGroupColor(sub.GrouId, sub.GroupRepeatNum), borderRadius: '10px' }),
                                                                    margin: '7px !important', zIndex: '20'
                                                                }}
                                                                className={(isSubActive ? 'active' : '') + ' subItem ' + (getGroupColor(sub.GrouId, sub.GroupRepeatNum) === '#0BC1B6' ? 'all-filled' : '')}
                                                                onClick={(e) => { e.stopPropagation(); updateIndex(index, subIndex) }}
                                                            >
                                                                <Typography className='sub-item' sx={{ display: 'flex' }}>
                                                                    <Box component="span" className="check-box">
                                                                        <CheckBox />
                                                                    </Box>
                                                                    {(language === 'en' ? sub.DescriptionEN : sub.DescriptionEL)}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </motion.div>
                                        }
                                    </AnimatePresence>

                                </Box>

                            </Box>
                        </ListItem>
                    );
                })}
            </List>


            {matchDownMD && (
                <Box sx={styles} className="mobile-sub-menu">
                    {formFields.map((category, index) => {
                        const isActive = index === activeIndex;
                        const subgroups = category.questions?.subgroups || [];

                        return (isActive) && (
                            <Box key={index}
                                sx={{ paddingRight: '0px' }}
                                className={(isActive ? 'active ' : '') + (subgroups.length > 0 ? 'with-sub' : '') + ' MuiListItem-root'}
                            >
                                <Box className='item'>
                                    <Box role="button" tabIndex={0} className="cnt-title" style={{ display: 'block', padding: '10px' }} onClick={() => { subgroups.length === 0 ? updateIndex(index) : updateIndex(index, 0) }}>
                                        {(subgroups.length > 0 && index === activeIndex) &&
                                            <Box
                                                sx={{
                                                    display: 'block',
                                                    border: {
                                                        xs: `0.6px solid ${groupColorsByIndex[index]} !important`,
                                                        sm: `0.6px solid ${groupColorsByIndex[index]} !important`,
                                                        md: 'none !important'
                                                    },
                                                    borderRadius: '8px'
                                                }}
                                                className="subgroups-cnt">

                                                {subgroups.map((sub, subIndex) => {
                                                    const isSubActive = index === activeIndex && activeIndexSubItem === subIndex;
                                                    return (
                                                        <Box
                                                            key={`sub-${index}-${subIndex}`}
                                                            ref={(el) => (subItemRefs.current[`${index}-${subIndex}`] = el)}

                                                            sx={{
                                                                ...((getGroupColor(sub.GrouId, sub.GroupRepeatNum) && (getGroupColor(sub.GrouId, sub.GroupRepeatNum) !== '#DAE0F0') ) && { border: '0.6px solid ' + getGroupColor(sub.GrouId, sub.GroupRepeatNum), borderRadius: '10px' }),
                                                                margin: '7px !important', zIndex: '20'
                                                            }}
                                                            className={(isSubActive ? 'active' : '') + ' subItem ' + (getGroupColor(sub.GrouId, sub.GroupRepeatNum) === '#0BC1B6' ? 'all-filled' : '')}
                                                            role="button" tabIndex={0} 
                                                            onClick={(e) => { e.stopPropagation(); updateIndex(index, subIndex) }}
                                                        >
                                                            <Typography className='sub-item' sx={{ display: 'flex' }}>
                                                                <Box component="span" className="check-box">
                                                                    <CheckBox />
                                                                </Box>
                                                                {(language === 'en' ? sub.DescriptionEN : sub.DescriptionEL)}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}

                                            </Box>
                                        }
                                    </Box>
                                </Box>

                            </Box>
                        );
                    })}
                </Box>
            )}


        </Box>
    );
}
